import React from 'react';
import LegalPage from './LegalPage';

export default function TermsOfService() {
  return (
    <LegalPage title="Terms of Service" updated="April 15, 2026">

      <p>Please read these Terms of Service ("Terms") carefully before using the ClickSaver platform and services operated by ClickSaver ("Company", "we", "us", or "our"). By accessing or using our service, you agree to be bound by these Terms.</p>

      <div className="legal-toc">
        <strong style={{ fontSize: 14, color: '#0f172a' }}>Table of Contents</strong>
        <ul style={{ marginTop: 10 }}>
          <li><a href="#acceptance">1. Acceptance of Terms</a></li>
          <li><a href="#service">2. Description of Service</a></li>
          <li><a href="#accounts">3. User Accounts</a></li>
          <li><a href="#billing">4. Billing &amp; Payment</a></li>
          <li><a href="#use">5. Acceptable Use</a></li>
          <li><a href="#ip">6. Intellectual Property</a></li>
          <li><a href="#disclaimer">7. Disclaimer of Warranties</a></li>
          <li><a href="#liability">8. Limitation of Liability</a></li>
          <li><a href="#termination">9. Termination</a></li>
          <li><a href="#governing">10. Governing Law</a></li>
          <li><a href="#contact">11. Contact</a></li>
        </ul>
      </div>

      <h2 id="acceptance">1. Acceptance of Terms</h2>
      <p>By creating an account or using any part of the ClickSaver service, you confirm that you are at least 18 years old, have the legal authority to enter into these Terms, and agree to be bound by them. If you are using the service on behalf of a company or organization, you represent that you have authority to bind that entity to these Terms.</p>

      <div className="legal-divider" />

      <h2 id="service">2. Description of Service</h2>
      <p>ClickSaver provides a click fraud detection and protection platform for digital advertisers. Our services include:</p>
      <ul>
        <li>Real-time analysis of click traffic on your websites</li>
        <li>Detection and scoring of fraudulent clicks</li>
        <li>Automatic IP exclusion management for Google Ads campaigns</li>
        <li>Fraud analytics dashboards and reports</li>
        <li>Email notifications and alerts</li>
      </ul>
      <p>We reserve the right to modify, suspend, or discontinue any part of the service at any time with reasonable notice.</p>

      <div className="legal-divider" />

      <h2 id="accounts">3. User Accounts</h2>
      <p>You are responsible for:</p>
      <ul>
        <li>Maintaining the confidentiality of your account credentials</li>
        <li>All activities that occur under your account</li>
        <li>Providing accurate and up-to-date account information</li>
        <li>Notifying us immediately of any unauthorized use of your account</li>
      </ul>
      <p>You may not share your account credentials or allow others to access your account. Each subscription is for a single user or organization account.</p>

      <div className="legal-divider" />

      <h2 id="billing">4. Billing &amp; Payment</h2>
      <h3>Subscription Plans</h3>
      <p>ClickSaver offers monthly subscription plans (Starter, Pro, Advanced). By subscribing, you authorize us to charge your payment method on a recurring basis.</p>

      <h3>Free Trial</h3>
      <p>New accounts receive a 7-day free trial. No payment information is required to start a trial. After the trial, you must subscribe to continue using the service.</p>

      <h3>Cancellation</h3>
      <p>You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period. No refunds are provided for unused portions of a billing period, except as outlined in our refund policy.</p>

      <h3>Refund Policy</h3>
      <p>If you are not satisfied with our service, we offer a full refund within 14 days of your first paid charge. After 14 days, all payments are non-refundable.</p>

      <h3>Price Changes</h3>
      <p>We may change our subscription prices with 30 days' advance notice. Continued use of the service after a price change constitutes acceptance of the new price.</p>

      <div className="legal-divider" />

      <h2 id="use">5. Acceptable Use</h2>
      <p>You agree not to use ClickSaver to:</p>
      <ul>
        <li>Violate any applicable laws or regulations</li>
        <li>Infringe on the intellectual property rights of others</li>
        <li>Transmit malware, viruses, or other harmful code</li>
        <li>Attempt to gain unauthorized access to our systems</li>
        <li>Use the service to monitor or block legitimate traffic</li>
        <li>Resell or sublicense the service without written permission</li>
        <li>Reverse engineer or attempt to extract source code from our platform</li>
      </ul>
      <p>Violation of these terms may result in immediate termination of your account.</p>

      <div className="legal-divider" />

      <h2 id="ip">6. Intellectual Property</h2>
      <p>All content, features, and functionality of ClickSaver — including but not limited to software, design, text, graphics, and logos — are owned by ClickSaver and are protected by intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our service without our prior written consent.</p>
      <p>You retain ownership of your data. By using our service, you grant us a limited license to use your data solely for the purpose of providing the service to you.</p>

      <div className="legal-divider" />

      <h2 id="disclaimer">7. Disclaimer of Warranties</h2>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. WE MAKE NO WARRANTY REGARDING THE ACCURACY OR COMPLETENESS OF FRAUD DETECTION RESULTS.</p>

      <div className="legal-divider" />

      <h2 id="liability">8. Limitation of Liability</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLICKSAVER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT PAID BY YOU IN THE 12 MONTHS PRECEDING THE CLAIM.</p>

      <div className="legal-divider" />

      <h2 id="termination">9. Termination</h2>
      <p>We may terminate or suspend your account at any time, with or without cause, with reasonable notice. Upon termination, your right to access the service ceases immediately. We may delete your data 30 days after account termination unless you request an earlier deletion or export.</p>

      <div className="legal-divider" />

      <h2 id="governing">10. Governing Law</h2>
      <p>These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising under these Terms shall be resolved through binding arbitration, except where prohibited by law.</p>

      <div className="legal-divider" />

      <h2 id="contact">11. Contact</h2>
      <p>For questions about these Terms, please contact us:</p>
      <ul>
        <li>Email: <a href="mailto:support@clicksaver.app">support@clicksaver.app</a></li>
        <li>Website: <a href="https://clicksaver.io">clicksaver.io</a></li>
      </ul>

    </LegalPage>
  );
}
